import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileDown } from "lucide-react";
import {
  CrewListPDFOptions,
  LocationFilter,
} from "@/lib/crew-list-pdf-generator";

interface ExportCrewListDialogProps {
  onExport: (options: CrewListPDFOptions) => Promise<void>;
  isExporting?: boolean;
}

export function ExportCrewListDialog({
  onExport,
  isExporting,
}: ExportCrewListDialogProps) {
  const [open, setOpen] = useState(false);
  const [includeContactData, setIncludeContactData] = useState(true);
  const [includeIntercomData, setIncludeIntercomData] = useState(false);
  const [locationFilter, setLocationFilter] = useState<LocationFilter>("all");

  const handleExport = async () => {
    await onExport({
      includeContactData,
      includeIntercomData,
      locationFilter,
    });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <FileDown className="h-4 w-4 mr-2" />
          Export Crew List PDF
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[360px]">
        <DialogHeader>
          <DialogTitle>Export Crew List PDF</DialogTitle>
          <DialogDescription>
            Configure what to include in the PDF export.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <Checkbox
              checked={includeContactData}
              onCheckedChange={(v) => setIncludeContactData(!!v)}
            />
            <div>
              <p className="text-sm font-medium">Include contact data</p>
              <p className="text-xs text-muted-foreground">
                Phone numbers and email addresses
              </p>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <Checkbox
              checked={includeIntercomData}
              onCheckedChange={(v) => setIncludeIntercomData(!!v)}
            />
            <div>
              <p className="text-sm font-medium">Export intercom data</p>
              <p className="text-xs text-muted-foreground">
                Unity hostname and user credentials
              </p>
            </div>
          </label>

          <div className="space-y-2">
            <Label htmlFor="export-location-filter">Location filter</Label>
            <Select
              value={locationFilter}
              onValueChange={(v) => setLocationFilter(v as LocationFilter)}
            >
              <SelectTrigger id="export-location-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="on_site">On-site only</SelectItem>
                <SelectItem value="remote">Remote only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={isExporting}>
            {isExporting ? "Exporting..." : "Export PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
